import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {NGXLogger} from 'ngx-logger';
import {UserService} from './user.service';
import {BoxService} from './box.service';
import { ENVIRONMENT } from '../../../models/src/lib/injection.tokens';
import { Pageable } from '../../../models/src/lib/base';
import { Subcategory } from '../../../models/src/lib/category';
import { Quote, QuoteItem } from '../../../models/src/lib/quote';
import { BoardType } from '../../../models/src/lib/types';

@Injectable({
    providedIn: 'root'
})
export class QuoteService {

    constructor(private userService: UserService,
                private boxService: BoxService,
                private httpClient: HttpClient,
                private logger: NGXLogger,
                            @Inject(ENVIRONMENT) private environment: any) {
    }

    load(quoteId: number, options: QuoteSearchOptions = {
        includeUser: true
    }): Promise<Quote> {
        return new Promise((resolve, reject) => {
            this.userService.getCurrentUser().then(user => {
                let url = `${this.environment.apiHost}/project/quote/${user.id}/${quoteId}?`;
                if (options.includeUser) {
                    url += `includeUser=${options.includeUser}&`;
                }
                this.httpClient.get(url)
                    .subscribe((response: any) => {
                        const result = new Quote().deserialize(response);
                        result.items.forEach(item => {
                            this.updatePaperTypes(item);
                        });
                        this.logger.debug('Got quote', result);
                        resolve(result);
                    }, error => {
                        this.logger.debug(error);
                        reject(error);
                    });
            }, reason => {
                this.logger.debug(reason);
                reject(reason);
            });
        });
    }

    private getConvertedQuote(quote: Quote): any {
        const items = quote.items.map(quoteItem => {
            return {
                id: quoteItem.id,
                length: quoteItem.length,
                width: quoteItem.width,
                depth: quoteItem.depth,
                boxCode: (quoteItem.subcategory?.code ?? "") + (quoteItem.boardType?.flute ?? ""),
                boardType: quoteItem.boardType?.flute,
                boardStrength: quoteItem.boardStrength?.code,
                paperType: quoteItem.paperType?.id,
                secondaryPaperType: quoteItem.secondaryPaperType?.id,
                gloss: quoteItem.gloss,
                luxe: quoteItem.luxe,
                preset: quoteItem.preset,
                quantity: quoteItem.quantity,
                twoSided: quoteItem.twoSided,
                expedited: quoteItem.expedited,
                container: quoteItem.container,

                pricePerUnit: quoteItem.pricePerUnit,
                totalPrice: quoteItem.totalPrice
            };
        });
        return {
            id: quote.id,
            name: quote.name,
            notes: quote.notes,
            orderId: quote.orderId,
            draftOrderId: quote.draftOrderId,
            poNumber: quote.poNumber,
            validUntil: quote.validUntil,
            items
        };
    }

    listQuotes(user: number, status: string, options: QuoteSearchOptions = {
        includeUser: false
    }): Promise<Pageable<Quote>> {
        return new Promise((resolve, reject) => {
            // this.logger.debug('Loading quotes');
            let url = `${this.environment.apiHost}/project/quote`;
            if (user) {
                url += `/${user}`;
            }
            url += `/list?`;
            if (status) {
                url += `status=${status}&`;
            }
            if (options.includeUser !== undefined) {
                url += `includeUser=${options.includeUser}&`;
            }
            if (options.page??0 > 0) {
                const offset = options.page??1 - 1;
                url += `offset=${offset}&limit=${options.pageSize}&`;
            }
            this.internalGetQuotes(url).then(value => {
                resolve(value);
            }, reason => {
                this.logger.debug(reason);
                reject(reason);
            });
        });
    }

    search(value: string, options: QuoteSearchOptions = {
        includeUser: true
    }): Promise<Pageable<Quote>> {
        return new Promise((resolve, reject) => {
            let url = `${this.environment.apiHost}/project/quote/search?value=${value}&`;
            if (options.includeUser !== undefined) {
                url += `includeUser=${options.includeUser}&`;
            }
            if (options.page??0 > 0) {
                const offset = options.page??1 - 1;
                url += `offset=${offset}&limit=${options.pageSize}&`;
            }

            this.internalGetQuotes(url).then(quote => {
                resolve(quote);
            }, reason => {
                this.logger.debug(reason);
                reject(reason);
            });
        });
    }

    private internalGetQuotes(url: string): Promise<Pageable<Quote>> {
        return new Promise((resolve, reject) => {
            this.httpClient.get(url)
                .subscribe((response: any) => {
                    const list: Quote[] = [];
                    (response.content as any[]).forEach((object) => {
                        const quote: Quote = new Quote().deserialize(object);
                        // console.log('quote', quote);
                        quote.items.forEach(item => {
                            this.updatePaperTypes(item);
                        });
                        list.push(quote);
                    });
                    const page: Pageable<Quote> = new Pageable<Quote>().deserialize(response);
                    page.content = list;
                    this.logger.debug('Loaded quotes', page);
                    resolve(page);
                }, error => {
                    this.logger.debug(error);
                    reject(error);
                });
        });
    }

    createQuote(user: number, quote: Quote): Promise<Quote> {
        return new Promise((resolve, reject) => {
            let url = `${this.environment.apiHost}/project/quote?`;
            if (user) {
                url += `user=${user}&`;
            }
            this.httpClient.post(url, this.getConvertedQuote(quote))
                .subscribe((response) => {
                    const result = new Quote().deserialize(response);
                    result.items.forEach(item => {
                        this.updatePaperTypes(item);
                    });
                    this.logger.debug('Stored quote', result);
                    resolve(result);
                }, error => {
                    this.logger.debug(error);
                    reject(error);
                });
        });
    }

    updateQuote(user: number, quote: Quote): Promise<Quote> {
        return new Promise((resolve, reject) => {
            let url = `${this.environment.apiHost}/project/quote/${quote.id}?`;
            if (user) {
                url += `user=${user}&`;
            }
            this.httpClient.put(url, this.getConvertedQuote(quote))
                .subscribe((response) => {
                    const result = new Quote().deserialize(response);
                    result.items.forEach(item => {
                        this.updatePaperTypes(item);
                    });
                    this.logger.debug('Updated quote', result);
                    resolve(result);
                }, error => {
                    this.logger.debug(error);
                    reject(error);
                });
        });
    }

    deleteQuote(quote: Quote): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.httpClient.delete(`${this.environment.apiHost}/project/quote/${quote.user?.id}/${quote.id}`)
                .subscribe(() => {
                    this.logger.debug('Deleted quote', quote);
                    resolve(true);
                }, error => {
                    this.logger.debug(error);
                    reject(error);
                });
        });
    }

    updatePaperTypes(item: QuoteItem) {

        // console.log('item.paperType', item.paperType, item.secondaryPaperType);
        if(!item.subcategory){
            item.subcategory=new Subcategory().deserialize({
                code: '',
                name: 'Custom Box',
                secondaryName: '',
                allowCustomSizes: false,
                boardType: new BoardType().deserialize({
                    code: item.flute,
                    name: item.flute,
                    flute: item.flute,
                    thickness: null,
                    boardStrengths: []
                }),
                displayPremiumWhite: false,
                displayWhite: false,
                displaySecondaryWhite: false,
                displayKraft: false,
                displaySecondaryKraft: false,
                displayTwoSided: false,
                displayLuxe: false,
                displayNoprint: false,
                displayStandard: false,
                allowCustomSample: false,
                hasVariants: false,

             })

        }
        const paperTypes = this.boxService.getPaperTypes(item.subcategory);
        paperTypes.forEach(paperType => {
            if (paperType.id === item.paperType?.id) {
                item.paperType = paperType;
                // console.log('matched primary', paperType);
                item.paperType.secondary.forEach(secondaryPaperType => {
                    // console.log('secondary match', secondaryPaperType.id, item.secondaryPaperType.id);
                    if (secondaryPaperType.id === item.secondaryPaperType?.id) {
                        item.secondaryPaperType = secondaryPaperType;
                    }
                });
            }
        });
    }

}

export interface QuoteSearchOptions {
    includeUser?: boolean;
    page?: number | 0;
    pageSize?: number | 10;
}
